import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, parse, isValid } from 'date-fns';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';
import { Textarea } from '@/components/ui/textarea';
import { Upload, X, ArrowLeft, Save, Trash2, CalendarDays, Users } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SearchableSelect from '@/components/SearchableSelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { COUNTRIES, EVENT_CATEGORIES } from '@/constants';

const EditEvent = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isIOSApp = useIsIOSApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isRecurringEvent, setIsRecurringEvent] = useState(false);
  const [showRecurringDialog, setShowRecurringDialog] = useState(false);
  const [seriesEventCount, setSeriesEventCount] = useState(0);
  const fileInputRef = useRef(null);
  const [myGroups, setMyGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    country: '',
    city: '',
    location_details: '',
    map_link: '',
    ticket_url: '',
    event_date: '',
    event_time: '',
    max_attendees: '',
    image_url: '',
    is_invite_only: false,
    is_shared_listing: false,
    external_host_name: '',
    external_source_url: '',
    auto_reminder_24h: false,
  });

  useEffect(() => {
    fetchEvent();
    fetchMyGroups();
  }, [eventId]);

  const fetchMyGroups = async () => {
    try {
      const res = await axios.get(`${API}/groups`);
      const allGroups = res.data || [];
      // Show all groups — creator should be able to assign any group
      setMyGroups(allGroups);
    } catch {}
  };

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      const event = response.data;
      
      // Check if user is the creator
      if (user && event.creator_id !== user.user_id) {
        toast.error('You can only edit your own events');
        navigate('/dashboard');
        return;
      }
      
      // Check if this is part of a recurring series
      const parentId = event.parent_event_id || event.event_id;
      if (event.parent_event_id || event.is_recurring) {
        setIsRecurringEvent(true);
        // Get count of future events in the series
        try {
          const allEventsRes = await axios.get(`${API}/events`);
          const allEvents = allEventsRes.data.events || allEventsRes.data || [];
          const seriesEvents = allEvents.filter(e => 
            (e.parent_event_id === parentId || e.event_id === parentId) &&
            e.event_date >= event.event_date
          );
          setSeriesEventCount(seriesEvents.length);
        } catch (err) {
          console.error('Failed to get series count', err);
        }
      }
      
      setFormData({
        title: event.title || '',
        description: event.description || '',
        category: event.category || '',
        country: event.country || '',
        city: event.city || '',
        location_details: event.location_details || '',
        map_link: event.map_link || '',
        ticket_url: event.ticket_url || '',
        event_date: event.event_date || '',
        event_time: event.event_time || '',
        max_attendees: event.max_attendees || '',
        image_url: event.image_url || '',
        is_invite_only: event.is_invite_only || false,
        is_shared_listing: event.is_shared_listing || false,
        external_host_name: event.external_host_name || '',
        external_source_url: event.external_source_url || '',
        auto_reminder_24h: event.auto_reminder_24h || false,
      });
      
      if (event.image_url) {
        setImagePreview(event.image_url);
      }
      
      // Restore selected groups
      const eventGroups = event.group_slugs || (event.group_slug ? [event.group_slug] : []);
      setSelectedGroups(eventGroups);
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGroupToggle = (groupSlug) => {
    setSelectedGroups(prev =>
      prev.includes(groupSlug) ? prev.filter(s => s !== groupSlug) : [...prev, groupSlug]
    );
  };

  const handleSelectAllGroups = () => {
    setSelectedGroups(prev =>
      prev.length === myGroups.length ? [] : myGroups.map(g => g.slug)
    );
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        const maxDim = 1200;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round(height * (maxDim / width));
            width = maxDim;
          } else {
            width = Math.round(width * (maxDim / height));
            height = maxDim;
          }
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const compressed = canvas.toDataURL('image/jpeg', 0.85);
        setImageFile(compressed);
        setImagePreview(compressed);
        setFormData(prev => ({ ...prev, image_url: '' }));
      };
      img.onerror = () => toast.error('Failed to process image');
      img.src = URL.createObjectURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If it's a recurring event, show the dialog to choose
    if (isRecurringEvent && seriesEventCount > 1) {
      setShowRecurringDialog(true);
      return;
    }
    
    // Otherwise, just update this event
    await saveEvent(false);
  };

  const saveEvent = async (updateSeries = false) => {
    setSaving(true);
    setShowRecurringDialog(false);

    try {
      const data = {
        ...formData,
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
        image_url: imageFile || formData.image_url,
        group_slug: selectedGroups[0] || null,
        group_slugs: selectedGroups.length > 0 ? selectedGroups : null,
        is_shared_listing: !!formData.is_shared_listing,
        external_host_name: formData.is_shared_listing ? (formData.external_host_name || '').trim() || null : null,
        external_source_url: formData.is_shared_listing ? (formData.external_source_url || '').trim() || null : null,
        auto_reminder_24h: !!formData.auto_reminder_24h,
      };
      
      if (updateSeries) {
        const response = await axios.put(`${API}/events/${eventId}/update-series`, data);
        toast.success(`Updated ${response.data.updated_count} events in the series!`);
      } else {
        await axios.put(`${API}/events/${eventId}`, data);
        toast.success('Event updated successfully!');
      }
      navigate(`/events/${eventId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event? This cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Event deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 pb-20 px-4 flex justify-center items-center">
          <div className="text-lg text-muted-foreground">Loading event...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-heading font-bold">Edit Event</h1>
                <p className="text-muted-foreground">Update your event details</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl border-2 border-border p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Event Image */}
                <div className="space-y-4">
                  <Label>Event Image</Label>
                  
                  {imagePreview ? (
                    <div className="relative">
                      <img 
                        src={imagePreview} 
                        alt="Event preview" 
                        className="w-full h-48 object-cover rounded-xl border-2"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2 rounded-full"
                        onClick={removeImage}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div 
                      className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm font-medium">Click to upload image</p>
                      <p className="text-xs text-muted-foreground">Max 2MB</p>
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={isIOSApp ? ".jpg,.jpeg,.png,.gif,.webp,.heic" : "image/*"}
                    onChange={handleImageFileChange}
                    className="hidden"
                  />

                  {!imagePreview && (
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Or paste an image URL</Label>
                      <Input
                        name="image_url"
                        type="url"
                        value={formData.image_url}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.value) {
                            setImagePreview(e.target.value);
                            setImageFile(null);
                          }
                        }}
                        placeholder="https://images.unsplash.com/..."
                        className="h-12 rounded-xl border-2"
                      />
                    </div>
                  )}
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-xl border-2"
                    placeholder="Give your event a catchy title"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    className="rounded-xl border-2 min-h-[180px]"
                    placeholder="Describe your event..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Tip: Press Enter for new paragraphs. Use - or • at the start of a line for bullet points.
                  </p>
                </div>

                {/* Category auto-set - no manual selection needed */}

                {/* Group Selector - Multi-select */}
                {myGroups.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Group(s)
                    </Label>
                    <div className="border-2 rounded-xl p-3 space-y-2 max-h-48 overflow-y-auto">
                      <label className="flex items-center gap-2 cursor-pointer pb-2 border-b">
                        <input
                          type="checkbox"
                          checked={selectedGroups.length === myGroups.length}
                          onChange={handleSelectAllGroups}
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                          data-testid="edit-select-all-groups"
                        />
                        <span className="text-sm font-semibold">All Groups</span>
                      </label>
                      {myGroups.map((group) => (
                        <label key={group.slug} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedGroups.includes(group.slug)}
                            onChange={() => handleGroupToggle(group.slug)}
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                            data-testid={`edit-group-checkbox-${group.slug}`}
                          />
                          <span className="text-sm">{group.name}</span>
                        </label>
                      ))}
                    </div>
                    {selectedGroups.length > 0 && (
                      <p className="text-xs text-primary">{selectedGroups.length} group{selectedGroups.length > 1 ? 's' : ''} selected</p>
                    )}
                  </div>
                )}

                {/* Location */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <SearchableSelect
                      options={COUNTRIES}
                      value={formData.country}
                      onValueChange={(value) => setFormData({ ...formData, country: value })}
                      placeholder="Select Country"
                      searchPlaceholder="Type to search country..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-xl border-2"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location_details">Location *</Label>
                  <Input
                    id="location_details"
                    name="location_details"
                    value={formData.location_details}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-xl border-2"
                    placeholder="Venue name, address, etc."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="map_link">Map Link (Optional)</Label>
                  <Input
                    id="map_link"
                    name="map_link"
                    type="url"
                    value={formData.map_link}
                    onChange={handleChange}
                    className="h-12 rounded-xl border-2"
                    placeholder="Google Maps link"
                  />
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="event_date">Date *</Label>
                    {/* Use shadcn Popover+Calendar so the picker works
                        reliably in TWA / Android webviews where the native
                        <input type="date"> can get stuck in segment-edit
                        mode without opening a picker. */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-12 w-full justify-start rounded-xl border-2 font-normal"
                          data-testid="edit-event-date-btn"
                        >
                          <CalendarDays className="w-4 h-4 mr-2 text-muted-foreground" />
                          {formData.event_date
                            ? (() => {
                                const parsed = parse(formData.event_date, 'yyyy-MM-dd', new Date());
                                return isValid(parsed) ? format(parsed, 'MMM d, yyyy') : formData.event_date;
                              })()
                            : <span className="text-muted-foreground">Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={(() => {
                            if (!formData.event_date) return undefined;
                            const parsed = parse(formData.event_date, 'yyyy-MM-dd', new Date());
                            return isValid(parsed) ? parsed : undefined;
                          })()}
                          onSelect={(d) => {
                            if (!d) return;
                            setFormData((prev) => ({ ...prev, event_date: format(d, 'yyyy-MM-dd') }));
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event_time">Time *</Label>
                    <Input
                      id="event_time"
                      name="event_time"
                      type="time"
                      value={formData.event_time}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-xl border-2"
                    />
                  </div>
                </div>

                {/* Max Attendees */}
                <div className="space-y-2">
                  <Label htmlFor="max_attendees">Max Attendees (Optional)</Label>
                  <Input
                    id="max_attendees"
                    name="max_attendees"
                    type="number"
                    value={formData.max_attendees}
                    onChange={handleChange}
                    className="h-12 rounded-xl border-2"
                    placeholder="Leave empty for unlimited"
                  />
                </div>

                {/* Invite Only Toggle */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl border-2">
                  <div>
                    <Label htmlFor="is_invite_only" className="font-semibold">Invite Only Event</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      People must request to join. You approve who attends.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData.is_invite_only}
                    onClick={() => setFormData(prev => ({ ...prev, is_invite_only: !prev.is_invite_only }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.is_invite_only ? 'bg-primary' : 'bg-gray-300'
                    }`}
                    data-testid="invite-only-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.is_invite_only ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Shared Listing Toggle */}
                <div className="p-4 bg-amber-50 rounded-xl border-2 border-amber-200 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label htmlFor="is_shared_listing" className="font-semibold">
                        I'm not the host — I'm sharing this event
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Tag this listing as third-party. We'll label it so members know you aren't running it.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={formData.is_shared_listing}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, is_shared_listing: !prev.is_shared_listing }))
                      }
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                        formData.is_shared_listing ? 'bg-amber-500' : 'bg-gray-300'
                      }`}
                      data-testid="shared-listing-toggle"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.is_shared_listing ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  {formData.is_shared_listing && (
                    <div className="space-y-3 pt-2 border-t border-amber-200" data-testid="shared-listing-fields">
                      <div className="space-y-2">
                        <Label htmlFor="external_host_name">Host / organizer name (optional)</Label>
                        <Input
                          id="external_host_name"
                          name="external_host_name"
                          value={formData.external_host_name}
                          onChange={handleChange}
                          placeholder="e.g. Berlin Startup Meetup"
                          className="h-12 rounded-xl border-2"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="external_source_url">Source / booking link (optional)</Label>
                        <Input
                          id="external_source_url"
                          name="external_source_url"
                          type="url"
                          value={formData.external_source_url}
                          onChange={handleChange}
                          placeholder="https://eventbrite.com/..."
                          className="h-12 rounded-xl border-2"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Auto 24h Reminder Toggle */}
                <div className="flex items-center justify-between p-4 bg-sky-50 rounded-xl border-2 border-sky-200">
                  <div>
                    <Label htmlFor="auto_reminder_24h" className="font-semibold">
                      Auto-reminder 24h before
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      If seats are still open, we'll nudge nearby members who haven't RSVP'd. One reminder per event, max.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData.auto_reminder_24h}
                    onClick={() => setFormData(prev => ({ ...prev, auto_reminder_24h: !prev.auto_reminder_24h }))}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                      formData.auto_reminder_24h ? 'bg-sky-500' : 'bg-gray-300'
                    }`}
                    data-testid="auto-reminder-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.auto_reminder_24h ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Ticket URL */}
                <div className="space-y-2">
                  <Label htmlFor="ticket_url">Ticket Purchase Link (Optional)</Label>
                  <Input
                    id="ticket_url"
                    name="ticket_url"
                    type="url"
                    value={formData.ticket_url}
                    onChange={handleChange}
                    className="h-12 rounded-xl border-2"
                    placeholder="https://eventbrite.com/your-event"
                  />
                  <p className="text-xs text-muted-foreground">
                    Add a link where attendees can purchase tickets
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-6">
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="h-12 px-6 rounded-full"
                  >
                    {deleting ? (
                      'Deleting...'
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Event
                      </>
                    )}
                  </Button>
                  
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 h-12 rounded-full font-bold"
                  >
                    {saving ? (
                      'Saving...'
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Recurring Event Update Dialog */}
      <Dialog open={showRecurringDialog} onOpenChange={setShowRecurringDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-primary" />
              Edit Recurring Event
            </DialogTitle>
            <DialogDescription>
              This event is part of a recurring series with {seriesEventCount} upcoming events. 
              How would you like to apply your changes?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-2 sm:flex-col">
            <Button
              onClick={() => saveEvent(false)}
              variant="outline"
              className="w-full justify-start"
              disabled={saving}
            >
              <span className="font-semibold">This event only</span>
              <span className="ml-2 text-muted-foreground text-sm">— Only update this single occurrence</span>
            </Button>
            <Button
              onClick={() => saveEvent(true)}
              className="w-full justify-start"
              disabled={saving}
            >
              <span className="font-semibold">This & all future events</span>
              <span className="ml-2 text-white/70 text-sm">— Update {seriesEventCount} events</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditEvent;
